/**
 * IndexedDB spool backend (TASK-2026-004 CR-01 A+, ADR-002 §2).
 *
 * - database `ccatlas-confirmation-spool`, version 1
 * - `records`: keyPath `['session_ref', 'id']`
 * - `meta`: keyPath `session_ref`
 *
 * 한 호출이 한 transaction이다. `transact`는 같은 readwrite transaction 안에서 meta와 기록
 * ID를 읽고, 동기 계획 함수의 결과를 그대로 쓴 뒤 `complete` 이벤트를 받아야 resolve한다.
 * 계획 함수 사이에 다른 비동기 작업을 끼우지 않으므로 transaction이 중간에 자동 commit되지
 * 않는다. 새 프로덕션 의존성은 없다.
 */

import {
  SpoolError,
  type SpoolBackend,
  type SpoolMeta,
  type SpoolMutation,
  type SpoolView,
} from './confirmationSpool';

export const SPOOL_DB_NAME = 'ccatlas-confirmation-spool';
export const SPOOL_DB_VERSION = 1;
const RECORDS = 'records';
const META = 'meta';
const MAX_ID = Number.MAX_SAFE_INTEGER;

function range(sessionRef: string): IDBKeyRange {
  return IDBKeyRange.bound([sessionRef, 0], [sessionRef, MAX_ID]);
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(SPOOL_DB_NAME, SPOOL_DB_VERSION);
    } catch {
      reject(new SpoolError('storage_unavailable'));
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(RECORDS)) {
        db.createObjectStore(RECORDS, { keyPath: ['session_ref', 'id'] });
      }
      if (!db.objectStoreNames.contains(META)) {
        db.createObjectStore(META, { keyPath: 'session_ref' });
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      // 다른 탭이 더 높은 version으로 열면 닫아 준다. 이 탭의 다음 호출은 실패로 드러난다.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    request.onerror = () => reject(new SpoolError('storage_unavailable'));
    request.onblocked = () => reject(new SpoolError('storage_unavailable'));
  });
}

/**
 * 브라우저 IndexedDB backend. `indexedDB`가 없으면 `undefined`다(`storage_unavailable`).
 *
 * database 열기는 처음 쓸 때 한 번 한다. 열기에 실패하면 이후 호출이 모두 같은 사유로 실패한다.
 */
export function createIndexedDbSpoolBackend(factory: IDBFactory | undefined = globalFactory()): SpoolBackend | undefined {
  if (factory === undefined) {
    return undefined;
  }
  let db: Promise<IDBDatabase> | undefined;
  const database = (): Promise<IDBDatabase> => {
    db ??= openDatabase(factory);
    return db;
  };

  return {
    async transact(sessionRef, plan) {
      const handle = await database();
      return new Promise<SpoolMeta>((resolve, reject) => {
        let tx: IDBTransaction;
        try {
          tx = handle.transaction([META, RECORDS], 'readwrite');
        } catch {
          reject(new SpoolError('write_failed'));
          return;
        }
        let mutation: SpoolMutation | undefined;
        let planError: unknown;
        const metaStore = tx.objectStore(META);
        const recordStore = tx.objectStore(RECORDS);
        const metaRequest = metaStore.get(sessionRef);
        const keysRequest = recordStore.getAllKeys(range(sessionRef));
        let pending = 2;

        const apply = () => {
          pending -= 1;
          if (pending > 0) {
            return;
          }
          const view: SpoolView = {
            meta: metaRequest.result as unknown,
            ids: (keysRequest.result as IDBValidKey[])
              .map((key) => (Array.isArray(key) ? Number(key[1]) : Number.NaN))
              .filter((id) => Number.isSafeInteger(id))
              .sort((a, b) => a - b),
          };
          try {
            mutation = plan(view);
          } catch (error) {
            planError = error;
            tx.abort();
            return;
          }
          for (const id of mutation.remove) {
            recordStore.delete([sessionRef, id]);
          }
          for (const item of mutation.put) {
            recordStore.put({ session_ref: sessionRef, id: item.id, record: item.record });
          }
          metaStore.put(mutation.meta);
        };
        metaRequest.onsuccess = apply;
        keysRequest.onsuccess = apply;
        tx.oncomplete = () => {
          if (mutation === undefined) {
            reject(new SpoolError('write_failed'));
            return;
          }
          resolve(mutation.meta);
        };
        tx.onabort = () => {
          reject(planError instanceof SpoolError ? planError : new SpoolError('write_failed'));
        };
        tx.onerror = () => {
          // onabort가 뒤따른다. 사유는 거기서 한 번만 알린다.
        };
      });
    },

    async list(sessionRef, limit) {
      let handle: IDBDatabase;
      try {
        handle = await database();
      } catch {
        throw new SpoolError('read_failed');
      }
      return new Promise((resolve, reject) => {
        let tx: IDBTransaction;
        try {
          tx = handle.transaction([META, RECORDS], 'readonly');
        } catch {
          reject(new SpoolError('read_failed'));
          return;
        }
        const metaRequest = tx.objectStore(META).get(sessionRef);
        const records: unknown[] = [];
        if (limit > 0) {
          const cursor = tx.objectStore(RECORDS).openCursor(range(sessionRef));
          cursor.onsuccess = () => {
            const current = cursor.result;
            if (current === null || records.length >= limit) {
              return;
            }
            const value = current.value as { id?: unknown; record?: unknown };
            records.push({ id: value.id, record: value.record });
            current.continue();
          };
        }
        tx.oncomplete = () => resolve({ meta: metaRequest.result as unknown, records });
        tx.onabort = () => reject(new SpoolError('read_failed'));
      });
    },
  };
}

function globalFactory(): IDBFactory | undefined {
  try {
    return typeof indexedDB === 'undefined' ? undefined : indexedDB;
  } catch {
    return undefined;
  }
}
